import { inject, Injectable } from "@angular/core";
import { catchError, from, map, Observable, switchMap, forkJoin } from "rxjs";
import { GraphQLService } from "app/shared/common/utils/graphql/graphql.service";
import { IRequisitionCandidateDetail, ReferenceCheckPayload, ReferenceCheckUpdatePayload } from "../interfaces";
import { APP_CONFIG } from "app.config";
import { HttpHelper } from "app/shared/common/utils/http/HttpHelper";
import { DmsService } from "app/shared/common/utils/DmsService";
import { STATUS_IN_DATABASE } from "app/shared/common/constants/Status.constant";
import { GRAPHQL_QUERY } from "app/shared/common/utils/graphql/graphql.query.constant";
import { CLASSIFICATION, QUALIFICATION_TYPE, SKILL_CATEGORY, SOURCE_TYPE } from "../../../task/workflow-task.constant";
import { LocalityKeyEnum } from "../constants";
import { UtilCommon } from "app/shared/common/utils/UtilCommon";

@Injectable({ providedIn: "root" })
export class CandidateService {
  #httpHelper: HttpHelper = inject(HttpHelper);
  #dmsService: DmsService = inject(DmsService);
  #graphQLService: GraphQLService = inject(GraphQLService);

  getEmployeeLevel() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryTatmLevelWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getSourceType() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, hrtm_source_category: { code: { _eq: SOURCE_TYPE.SOURCE_TYPE } } };
    const query = GRAPHQL_QUERY.hrmQueryHrtmSourceTypeWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getSource(sourceTypeId: number) {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, hrtm_source_category: { code: { _eq: SOURCE_TYPE.SOURCE } }, parentId: { _eq: sourceTypeId } };
    const query = GRAPHQL_QUERY.hrmQueryHrtmSourceTypeWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getAllSources() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, hrtm_source_category: { code: { _eq: SOURCE_TYPE.SOURCE } } };
    const query = GRAPHQL_QUERY.hrmQueryHrtmSourceTypeWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getAllSourceTypes() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryHrtmSourceTypeWithSourceCategoryWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getAllChannels() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryRstmChannelWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getContactType() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryHrtbWorkerRelationWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getCountries() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryPltmCountryWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getQualifications() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryTatmQualificationTypeWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getQualificationTypes() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, code: { _in: [QUALIFICATION_TYPE.CER, QUALIFICATION_TYPE.DEG] } };
    const query = GRAPHQL_QUERY.hrmQueryTatmQualificationTypeWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getQualificationDeg() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, tatm_qualification_type: { code: { _eq: QUALIFICATION_TYPE.DEG } } };
    const query = GRAPHQL_QUERY.hrmQueryTatmQualificationWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getUniversities() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryTatmSchoolWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getMajors() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryTatmMajoringWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getFaculties() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryTatmFacultyWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getQualificationRates() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryTatmQualificationRateWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getIndustries() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryTatmIndustryWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getJobTypes() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryTatmTypeWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getJobTitles() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryTatmTitleWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getOffices() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryHrtbOfficeWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getRelations() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryHrtmRelationWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getJobTracks() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, tatm_classification: { code: { _eq: CLASSIFICATION.TRACK } } };
    const query = GRAPHQL_QUERY.hrmQueryTatbJobWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getJobFamilies() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, tatm_classification: { code: { _eq: CLASSIFICATION.FAMILY } } };
    const query = GRAPHQL_QUERY.hrmQueryTatbJobWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getJobGroups() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, tatm_classification: { code: { _eq: CLASSIFICATION.GROUP } } };
    const query = GRAPHQL_QUERY.hrmQueryTatbJobWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getSubGroups() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, tatm_classification: { code: { _eq: CLASSIFICATION.SUBGROUP } } };
    const query = GRAPHQL_QUERY.hrmQueryTatbJobWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getSkills() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, tatm_skill_category: { code: { _eq: SKILL_CATEGORY.SKILL } } };
    const query = GRAPHQL_QUERY.hrmQueryTatmSkillWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getNationalities() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryHrtmNationalityWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getLegalDocTypes() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryHrtmLegaldocTypeWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getIssuePlaces() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryPltmIssuingAuthoritiesWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getCity() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, pltm_locality_type: { code: { _eq: LocalityKeyEnum.PROVINCE } } };
    const query = GRAPHQL_QUERY.hrmQueryPltmLocalityWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getAllEmployees() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryHrtbEmployeeWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getRequisitionCandidateWithInterviewByCandidateId(candidateIds: any) {
    const where = { candidateId: { _in: candidateIds } };
    const query = GRAPHQL_QUERY.hrmQueryRstbRequisitionCandidateWithInterviewWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })
    ).pipe(map((data: any) => data?.data))
  }

  /**
   * @param requisitionIds
   * @returns Observable with filtered requisition data
   */
  filterRequisitionByBU(requisitionIds: number[]): Observable<any> {
    const url = `${APP_CONFIG.MCRHRRCRT}requisitionCandidate/filterRequisitionByBU`;
    return this.#httpHelper.methodPostObservableService(url, requisitionIds);
  }

  getAttachmentTypes() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryHrtmAttachmentTypeWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getMarriedStatus() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryHrtmMaritalStatusWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getSummaryType() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryHrtmSummaryTypeWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getWorkerAttachment(where: any) {
    const query = GRAPHQL_QUERY.hrmQueryHrtbWorkerAttachmentWhere.query;
    return from(this.#graphQLService.mutatePromiseWithVariable(query, {
      where
    })).pipe(
      catchError((error) => {
        return error;
      }))
  }

  getRequisitionCandidateDetail(candidateId: number): Observable<IRequisitionCandidateDetail> {
    const where = { candidateId: { _eq: candidateId } };
    const query = GRAPHQL_QUERY.hrmQueryRstbRequisitionCandidateWhere.query;
    return from(this.#graphQLService.mutatePromiseWithVariable(query, {
      where
    })).pipe(
      map((data: any) => data?.data?.[0] ?? null)
    )
  }

  getCandidateResolver() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where1 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where2 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, hrtm_source_category: { code: { _eq: SOURCE_TYPE.SOURCE_TYPE } } };
    const where3 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, hrtm_source_category: { code: { _eq: SOURCE_TYPE.SOURCE } } };
    const where4 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where5 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where6 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, code: { _in: [QUALIFICATION_TYPE.CER, QUALIFICATION_TYPE.DEG] } };
    const where7 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where8 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, tatm_qualification_type: { code: { _eq: QUALIFICATION_TYPE.DEG } } };
    const where9 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where10 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where11 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where12 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where13 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where14 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryCandidateResolver.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where, where1, where2, where3, where4, where5, where6, where7, where8, where9, where10, where11, where12, where13, where14
      })).pipe(map((data: any) => data));
  }

  getCandidateExtResolver() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where1 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, tatm_classification: { code: { _eq: CLASSIFICATION.TRACK } } };
    const where2 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, tatm_classification: { code: { _eq: CLASSIFICATION.FAMILY } } };
    const where3 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, tatm_classification: { code: { _eq: CLASSIFICATION.GROUP } } };
    const where4 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, tatm_classification: { code: { _eq: CLASSIFICATION.SUBGROUP } } };
    const where5 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where6 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where7 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where9 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where10 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where11 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where12 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where13 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const where14 = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE }, pltm_locality_type: { code: { _eq: LocalityKeyEnum.PROVINCE } } };
    const query = GRAPHQL_QUERY.hrmQueryCandidateExtResolver.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where, where1, where2, where3, where4, where5, where6, where7, where9, where10, where11, where12, where13, where14
      })).pipe(map((data: any) => data));
  }

  getGenders() {
    const where = { recordStatus: { _eq: STATUS_IN_DATABASE.ACTIVE } };
    const query = GRAPHQL_QUERY.hrmQueryTatmGenderWhere.query;
    return from(
      this.#graphQLService.mutatePromiseWithVariable(query, {
        where
      })).pipe(map((data: any) => data?.data || []));
  }

  getWorkerByCandidateId(candidateId: number) {
    const where = { hrtb_candidates: { id: { _eq: candidateId } } };
    const query = GRAPHQL_QUERY.hrmQueryHrtbWorkerWhere.query;
    return from(this.#graphQLService.mutatePromiseWithVariable(query, {
      where
    }))
  }

  getRequisitionCandidateRecommender(candidateId: number, requisitionCodes: any[]) {
    // const where = { candidateId: { _eq: candidateId }, rstb_requisition: { code: { _in: requisitionCodes } } };
    const _eq = candidateId;
    const _in = requisitionCodes;
    const query = GRAPHQL_QUERY.hrmQueryRstbRequisitionCandidateWithRecommenderWhere.query;
    return from(this.#graphQLService.mutatePromiseWithVariable(query, {
      _eq, _in
    })).pipe(
      map((data: any) => data?.data || [])
    )
  }

  getUserSocialStatus(where: any): Promise<any> {
    // emailRef: {_contains: "hieutest10+7@maildrop.cc"}, userStatus: {_eq: "A"}}
    const query = GRAPHQL_QUERY.hrmQuerySmtbUserSocialStatusWhere.query;
    return this.#graphQLService.mutatePromiseWithVariable(query, {
      where: where
    });
  }

  getAssignmentsByRequisitionCandidateId(id: number) {
    const where = { requisitionCandidateId: { _eq: id } };
    const query = GRAPHQL_QUERY.hrmQueryRstbAssignmentWhere.query;
    return from(this.#graphQLService.mutatePromiseWithVariable(query, {
      where
    })).pipe();
  }

  /**
   * Get assignments by Requisition Candidate ID
   * @param requisitionCandidateId - The requisition candidate ID
   * @returns Observable with assignment data
   */
  getAssignmentByRequisitionCandidateId(requisitionCandidateId: number): Observable<any> {
    const url = `${APP_CONFIG.MCRHRRCRT}assignment/getByRequisitionCandidateId?requisitionCandidateId=${requisitionCandidateId}`;
    return this.#httpHelper.methodGetObservableService(url);
  }

  getAssignmentByCandidateId(candidateId: number): Observable<any> {
    const url = `${APP_CONFIG.MCRHRRCRT}assignment/getByCandidateId?candidateId=${candidateId}`;
    return this.#httpHelper.methodGetObservableService(url);
  }

  getReferenceCheckByRequisitionCandidateId(requisitionCandidateId: number): Observable<any> {
    const url = `${APP_CONFIG.MCRHRRCRT}recruit/referenceCheck/getByRequisitionCandidateId?requisitionCandidateId=${requisitionCandidateId}`;
    return this.#httpHelper.methodGetObservableService(url);
  }

  getReferenceCheckByCandidateId(candidateId: number): Observable<any> {
    const url = `${APP_CONFIG.MCRHRRCRT}recruit/referenceCheck/getByCandidateId?candidateId=${candidateId}`;
    return this.#httpHelper.methodGetObservableService(url);
  }

  checkExistingEmail(email: string) {
    const where = { personalEmail: { _eq: email } };
    const query = GRAPHQL_QUERY.hrmQueryHrtbWorkerWhere.query;
    return from(this.#graphQLService.mutatePromiseWithVariable(query, {
      where
    })).pipe(
      map((data: any) => !!data?.data?.length)
    )
  }

  getWorkerDetailByAggId(aggId: number) {
    const _eq = aggId;
    const recordStatus = STATUS_IN_DATABASE.ACTIVE;
    const queryWorker = GRAPHQL_QUERY.hrmQueryHrtbWorkerForDetailWhere.query;
    const queryCandidate = GRAPHQL_QUERY.hrmQueryHrtbCandidateForWorkerDetailWhere.query;
    const queryWorkerQualification = GRAPHQL_QUERY.hrmQueryHrtbWorkerQualificationForWorkerDetailWhere.query;
    const queryWorkerRecord = GRAPHQL_QUERY.hrmQueryHrtbWorkerRecordForWorkerDetailWhere.query;
    const queryWorkerRefperson = GRAPHQL_QUERY.hrmQueryHrtbWorkerRefpersonForWorkerDetailWhere.query;
    const queryWorkerAttachment = GRAPHQL_QUERY.hrmQueryHrtbWorkerAttachmentForWorkerDetailWhere.query;
    const queryWorkerLegaldoc = GRAPHQL_QUERY.hrmQueryHrtbWorkerLegaldocForWorkerDetailWhere.query;
    const queryWorkerSummary = GRAPHQL_QUERY.hrmQueryHrtbWorkerSummaryForWorkerDetailWhere.query;
    const queryWorkerAddress = GRAPHQL_QUERY.hrmQueryHrtbWorkerAddressForWorkerDetailWhere.query;
    const queryWorkerSocial = GRAPHQL_QUERY.hrmQueryHrtbWorkerSocialForWorkerDetailWhere.query;
    return forkJoin([
      this.#graphQLService.mutatePromiseWithVariable(queryWorker, { _eq }),
      this.#graphQLService.mutatePromiseWithVariable(queryCandidate, { _eq, recordStatus }),
      this.#graphQLService.mutatePromiseWithVariable(queryWorkerQualification, { _eq, recordStatus }),
      this.#graphQLService.mutatePromiseWithVariable(queryWorkerRecord, { _eq, recordStatus }),
      this.#graphQLService.mutatePromiseWithVariable(queryWorkerRefperson, { _eq, recordStatus }),
      this.#graphQLService.mutatePromiseWithVariable(queryWorkerAttachment, { _eq, recordStatus }),
      this.#graphQLService.mutatePromiseWithVariable(queryWorkerLegaldoc, { _eq, recordStatus }),
      this.#graphQLService.mutatePromiseWithVariable(queryWorkerSummary, { _eq, recordStatus }),
      this.#graphQLService.mutatePromiseWithVariable(queryWorkerAddress, { _eq, recordStatus }),
      this.#graphQLService.mutatePromiseWithVariable(queryWorkerSocial, { _eq, recordStatus })
    ]).pipe(
      map(results => {
        const worker = UtilCommon.isArray(results[0]?.worker) ? { ...results[0]?.worker[0] } : null;
        const candidate = results[1] || {};
        const workerQualification = results[2] || {};
        const workerRecord = results[3] || {};
        const workerRefperson = results[4] || {};
        const workerAttachment = results[5] || {};
        const workerLegaldoc = results[6] || {};
        const workerSummary = results[7] || {};
        const workerAddress = results[8] || {};
        const workerSocial = results[9] || {};
        if (!worker) {
          return null;
        }
        const detail = { ...worker, ...candidate, ...workerQualification, ...workerRecord, ...workerRefperson, ...workerAttachment, ...workerLegaldoc, ...workerSummary, ...workerAddress, ...workerSocial }
        return [detail];
      })
    );
  }

  getDataCandidateGroup(aggId: number): Observable<any[]> {
    const _eq = aggId;
    const recordStatus = STATUS_IN_DATABASE.ACTIVE;
    const query = GRAPHQL_QUERY.hrmQueryHrtbWorkerForCandidateGroupWhere.query;
    return from(this.#graphQLService.mutatePromiseWithVariable(query, {
      _eq, recordStatus
    })).pipe(
      map((data: any) => data?.worker ?? null)
    );
  }

  pagingAssignedRequisitionPromise(startRow: any = 0, endRow: any = 50): Promise<any> {
    const url = `${APP_CONFIG.MCRHRRCRT}requisition/pivotPagingAssignedRequistion`;
    const params: any = {
      "startRow": startRow,
      "endRow": endRow,
      "rowGroupCols": [],
      "valueCols": [],
      "pivotCols": [],
      "pivotMode": true,
      "filterModel": {
        "status": {
          "type": "equals",
          "filter": "INPROCESS",
          "filterType": "text"
        }
      },
      "sortModel": [
        { "colId": "createTime", "sort": "desc" },
        { "colId": "statusPriority", "sort": "asc" }
      ]
    }
    return this.#httpHelper.methodPostJsonService(url, params);
  }

  pagingAssignedRequisition(keyword: string = '') {
    const URL = `${APP_CONFIG.MCRHRRCRT}requisition/pivotPagingAssignedRequistion`;
    const URL_COUNT = `${APP_CONFIG.MCRHRRCRT}requisition/pivotCountAssignedRequistion`;
    const payload: any = {
      "startRow": 0,
      "endRow": 30,
      "rowGroupCols": [],
      "valueCols": [],
      "pivotCols": [],
      "pivotMode": true,
      "filterModel": {
        "status": {
          "type": "equals",
          "filter": "INPROCESS",
          "filterType": "text"
        }
      },
      "sortModel": [
        { "colId": "createTime", "sort": "desc" },
        { "colId": "statusPriority", "sort": "asc" }
      ]
    }

    if (keyword) {
      payload.filterModel.ftsString = { "type": "contains", "filter": keyword, "filterType": "text" };
    }

    return this.#httpHelper.methodPostObservableService(URL_COUNT, payload).pipe(
      switchMap((response) => {
        payload.endRow = response?.data;
        return this.#httpHelper.methodPostObservableService(URL, payload).pipe(
          map((res) => res?.data)
        )
      })
    )
  }

  pivotPagingByBUFilter(candidateId: number, startRow: any = 0, endRow: any = 50) {
    const url = `${APP_CONFIG.MCRHRRCRT}requisitionCandidate/pivotPagingByBUFilter`;
    const params: any = {
      "startRow": startRow,
      "endRow": endRow,
      "rowGroupCols": [],
      "valueCols": [],
      "pivotCols": [],
      "pivotMode": true,
      "filterModel": {
        "candidateId": {
          "type": "equals",
          "filter": candidateId,
          "filterType": "number"
        }
      },
      "sortModel": [
        { "colId": "createDate", "sort": "desc" }
      ]
    }
    return this.#httpHelper.methodPostObservableService(url, params);
  }

  createReferenceCheck(payload: ReferenceCheckPayload) {
    const url = `${APP_CONFIG.MCRHRRCRT}recruit/referenceCheck/create`;
    return this.#httpHelper.methodPostObservableService(url, payload)
  }

  updateReferenceCheck(payload: ReferenceCheckUpdatePayload) {
    const url = `${APP_CONFIG.MCRHRRCRT}recruit/referenceCheck/update`
    return this.#httpHelper.methodPostObservableService(url, payload)
  }

  printReferenceCheck(aggId: string) {
    const url = `${APP_CONFIG.MCRHRRCRT}recruit/referenceCheck/print`
    return this.#httpHelper.methodPostObservableService(url, { aggId })
  }

  downloadReferenceCheckAttachment(identify: string, responseType: string = 'blob') {
    const url = `${APP_CONFIG.MCRHRDMS}profile/referenceCheck/download`
    return from(this.#dmsService.getAttachmentFromDms(url, identify))
  }
}
